import { useContext, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { AuthContext } from '../../../contexts/AuthContext';
import { castVote, subscribeOwnVotes } from '../../../database/tournamentVotes';
import { voteKey } from '../../../lib/stages';
import { entryByline, isEntryMember } from '../../../lib/entries';
import { handleFirebaseError } from '../../../utils/errorHandler';
import RatingScale from '../tournament/RatingScale';

const CommunityVotePanel = ({ tournamentId, stage, playersById, players }) => {
  const { currentUser } = useContext(AuthContext);
  const [own, setOwn] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!currentUser) {
      setOwn({});
      return undefined;
    }
    return subscribeOwnVotes(tournamentId, currentUser.uid, setOwn, () => setOwn({}));
  }, [tournamentId, currentUser]);

  const isCompetitor =
    !!currentUser && players.some((player) => isEntryMember(player, currentUser.uid));
  const canVote = !!currentUser && !isCompetitor;

  const handleChange = async (playerId, rating) => {
    setSaving(true);
    try {
      await castVote(tournamentId, currentUser, voteKey(stage.id, playerId), rating);
      toast.success('Vote saved.');
    } catch (err) {
      toast.error('Could not save your vote.', { description: handleFirebaseError(err) });
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="space-y-3 rounded-md border border-border/60 p-4 sm:p-5">
      <h3 className="font-display text-base font-semibold">Community vote</h3>
      <p className="text-sm text-muted-foreground">
        Rate each entry from 1 to 10. You can change your rating until voting closes.
      </p>
      {!currentUser && (
        <p className="text-xs text-muted-foreground">
          <Link to="/login" className="text-heat underline-offset-2 hover:underline">
            Log in to vote.
          </Link>
        </p>
      )}
      {isCompetitor && (
        <p className="text-xs text-muted-foreground">
          You have an entry in this contest, so you can't vote.
        </p>
      )}
      <ul className="divide-y divide-border/60">
        {(stage.participants || [])
          .filter((id) => playersById[id])
          .map((playerId) => (
            <li
              key={playerId}
              className="flex flex-col gap-2 py-3 sm:flex-row sm:items-center sm:justify-between"
            >
              <div className="min-w-0">
                <div className="truncate font-medium">{playersById[playerId].name}</div>
                {entryByline(playersById[playerId]) && (
                  <div className="truncate text-xs text-muted-foreground">
                    by {entryByline(playersById[playerId])}
                  </div>
                )}
              </div>
              <RatingScale
                className="sm:w-80"
                label="Rate this entry from 1 to 10"
                value={own[voteKey(stage.id, playerId)] || 0}
                disabled={!canVote || saving}
                onChange={(rating) => handleChange(playerId, rating)}
              />
            </li>
          ))}
      </ul>
    </section>
  );
};

export default CommunityVotePanel;
